import { useState, useRef } from 'react';

import { AxiosResponse } from 'axios';

import i18n from 'i18next';

import { useSnackbar } from 'notistack';

import { FormHandles } from '@unform/core';

import { applyMask } from '../utils';

import { getParameters, updateParametersAPI } from '../api';

import {
  IParameterData,
  IParametersHook,
  IParamApi,
  IParamsUpdadeData,
} from '../types';

export const useParametersHooks = (): IParametersHook => {
  const clientId = Number(localStorage.getItem('@pdamodules::codigoCliente'));

  const { enqueueSnackbar } = useSnackbar();

  const searchRef = useRef<FormHandles>(null);

  const parametersRef = useRef<IParameterData[]>([]);

  const [parameters, setParameters] = useState<IParameterData[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showTable, setShowTable] = useState<boolean>(false);

  const hasEmptyOrUndefinedValue = (): boolean =>
    parameters.some(p => p.valor === '' || p.valor === undefined);

  const handleSaveChanges = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const notEmptyValues = hasEmptyOrUndefinedValue();

      if (notEmptyValues) {
        enqueueSnackbar(String(i18n.t('screens.parameters.errorMessage')), {
          variant: 'warning',
        });
        return;
      }

      const transformDataUpadate: IParamsUpdadeData[] = parameters.map(
        (params: IParameterData) => {
          return {
            codigoCliente: clientId,
            valor: params.valor || '',
            codigoParametro: params.codigoParametro,
          };
        }
      );

      await updateParametersAPI(transformDataUpadate).then(() => {
        enqueueSnackbar(String(i18n.t('screens.parameters.saveduccessfully')), {
          variant: 'success',
        });
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearParameter = (rowTable: IParameterData): void => {
    setParameters((prevState: IParameterData[]) => {
      return prevState.map(p =>
        p.codigoParametro === rowTable.codigoParametro ? { ...p, valor: '' } : p
      );
    });
  };

  const handleUpdateParameters = (
    rowTable: IParameterData,
    value?: string
  ): void => {
    const sendValue = value || '';

    const mask = applyMask(rowTable.descricaoObservacao, sendValue);

    setParameters((prevState: IParameterData[]) => {
      return prevState.map(p =>
        p.codigoParametro === rowTable.codigoParametro
          ? { ...p, valor: mask || '' }
          : p
      );
    });
  };

  const handleSearch = async (description?: string) => {
    try {
      setIsLoading(true);
      setShowTable(false);

      const params: IParamApi = {
        descricao: description || undefined,
        codigoCliente: clientId,
      };
      await getParameters(params).then((res: AxiosResponse) => {
        if (!res.data.length) {
          enqueueSnackbar(String(i18n.t('components.table.notFound')), {
            variant: 'warning',
          });

          setParameters([]);
          parametersRef.current = [];

          return;
        }
        const response: IParameterData[] = res.data;

        const reordered = response.sort((a, b) => {
          const aHasSimNao =
            /sim/i.test(a.descricaoObservacao) &&
            /não/i.test(a.descricaoObservacao);
          const bHasSimNao =
            /sim/i.test(b.descricaoObservacao) &&
            /não/i.test(b.descricaoObservacao);

          return Number(aHasSimNao) - Number(bHasSimNao);
        });

        setParameters(reordered);

        enqueueSnackbar(
          `${response.length} ${String(i18n.t('screens.user.resultFound'))}`,
          {
            variant: 'success',
          }
        );
      });
    } finally {
      setIsLoading(false);
      setShowTable(true);
    }
  };

  return {
    searchRef,
    isLoading,
    showTable,
    parameters,
    handleSearch,
    handleSaveChanges,
    handleClearParameter,
    handleUpdateParameters,
  };
};
